import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, Building2, ArrowRight, CheckCircle2 } from 'lucide-react';
import { SectionHeader, FilterPills, EmptyState, PageSkeleton } from '@/components/dashboard/MetricCard';
import FundingBar from '@/components/opportunities/FundingBar';
import {
  opportunitiesApi, STATUS_STYLE, imgUrl, money, opTitle, opPlace, returnFigure, saleFigure, statusLabel,
} from '@/services/opportunitiesApi';
import { useSiteLang } from '@/components/site/SiteLang';

/*
  Oportunidades del club: operaciones reales con sus cifras (rentabilidad estimada, plazo, inversión,
  aportación mínima) y la barra de progreso de las aportaciones. «Ver más» abre la ficha completa.
  Las fichas vienen de la BD en español: en inglés se muestran tal cual, con el aviso «Content delivered in Spanish».
*/

const T = {
  es: {
    requestSent: 'Solicitud enviada',
    term: 'Plazo estimado',
    totalInvestment: 'Inversión total estimada',
    minContribution: 'Aportación mínima',
    more: 'Ver más',
    title: 'Oportunidades',
    subtitle: 'Operaciones presentadas a los socios del club. Cifras estimadas y orientativas, sin garantía de resultados.',
    loadError: 'No hemos podido cargar las oportunidades',
    empty: 'No hay oportunidades publicadas',
    emptyText: 'Te avisaremos cuando se abra la próxima operación.',
  },
  en: {
    requestSent: 'Request sent',
    term: 'Estimated term',
    totalInvestment: 'Estimated total investment',
    minContribution: 'Minimum contribution',
    more: 'View details',
    title: 'Opportunities',
    subtitle: 'Deals presented to club members. Figures are estimates for guidance only, with no guarantee of results.',
    loadError: 'We couldn’t load the opportunities',
    empty: 'No opportunities published yet',
    emptyText: 'We’ll let you know when the next deal opens.',
  },
};

function Stat({ label, value, accent }) {
  if (!value) return null;
  return (
    <div className="min-w-0">
      <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold leading-tight">{label}</p>
      <p className={`mt-0.5 text-sm font-semibold tabular-nums leading-snug ${accent ? 'text-[#e3c87a]' : 'text-white'}`}>{value}</p>
    </div>
  );
}

function OpportunityCard({ o, i, t, lang }) {
  const ret = returnFigure(o, lang);
  const sale = saleFigure(o, lang);
  const img = imgUrl(o.images?.[0]);
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: Math.min(i, 6) * 0.05 }}>
      <Link to={`/dashboard/propiedades/${encodeURIComponent(o.id)}`} data-testid={`opportunity-${o.id}`}
        className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:border-[#c9a84c]/30 transition-colors duration-300">
        <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-[#111b30] to-[#0a1225]">
          {img ? <img src={img} alt={opTitle(o, lang)} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
            : <div className="flex h-full items-center justify-center"><Building2 className="text-[#c9a84c]/25" size={46} /></div>}
          <div className="absolute inset-0 bg-gradient-to-t from-[#060d1b] via-[#060d1b]/10 to-transparent" />
          <span className={`absolute top-3 left-3 rounded-full border px-2.5 py-1 text-[11px] font-semibold backdrop-blur ${STATUS_STYLE[o.status] || STATUS_STYLE['Ventana cerrada']}`}>{statusLabel(o.status, lang)}</span>
          {o.my_interest && (
            <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-semibold text-emerald-300 backdrop-blur">
              <CheckCircle2 size={12} /> {t.requestSent}
            </span>
          )}
          <div className="absolute bottom-3 left-4 right-4">
            <p className="flex items-center gap-1.5 text-xs text-gray-200"><MapPin size={12} className="text-[#c9a84c]" /> {opPlace(o)}</p>
          </div>
        </div>
        <div className="flex flex-1 flex-col gap-4 p-5">
          <div>
            <h3 className="text-white font-semibold leading-snug group-hover:text-[#e3c87a] transition-colors">{opTitle(o, lang)}</h3>
            {o.tagline && <p className="text-xs text-gray-400 mt-0.5">{o.tagline}</p>}
          </div>
          {ret && (
            <div className="rounded-xl border border-[#c9a84c]/20 bg-[#c9a84c]/[0.06] px-3.5 py-2.5">
              <p className="text-[10px] uppercase tracking-wider text-[#c9a84c] font-semibold">{ret.label}</p>
              <p className="text-xl font-bold text-[#e3c87a] tabular-nums">{ret.value}</p>
            </div>
          )}
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <Stat label={t.term} value={o.term} />
            <Stat label={t.totalInvestment} value={o.total_investment ? money(o.total_investment, o.currency) : null} />
            {sale && <Stat label={sale.label} value={sale.value} />}
            {(o.progress?.pct ?? 0) < 100 && <Stat label={t.minContribution} value={money(o.min_contribution, o.currency)} accent />}
          </div>
          <div className="mt-auto space-y-4">
            <FundingBar progress={o.progress} currency={o.currency} delay={0.2 + Math.min(i, 6) * 0.05} />
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-[#c9a84c]">
              {t.more} <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function DashboardPropiedades() {
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('Todas');

  useEffect(() => { opportunitiesApi.list().then(setItems).catch((e) => { setError(e.message); setItems([]); }); }, []);
  const statuses = useMemo(() => ['Todas', ...new Set((items || []).map((o) => o.status))], [items]);
  if (!items) return <PageSkeleton cards={3} rows={2} />;
  const shown = items.filter((o) => filter === 'Todas' || o.status === filter);

  return (
    <div className="space-y-6" data-testid="dashboard-propiedades">
      <SectionHeader title={t.title} subtitle={t.subtitle} />
      {lang === 'en' && shown.length > 0 && <p className="-mt-4 text-[11px] text-gray-500" data-testid="content-lang-note">Content delivered in Spanish</p>}
      {statuses.length > 2 && <FilterPills options={statuses} active={filter} onChange={setFilter} />}
      {shown.length === 0 ? (
        <EmptyState icon={Building2} title={error ? t.loadError : t.empty} text={error || t.emptyText} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {shown.map((o, i) => <OpportunityCard key={o.id} o={o} i={i} t={t} lang={lang} />)}
        </div>
      )}
    </div>
  );
}
